/**
 * WizardStepSources — Step 1 of the New Transformation wizard.
 * Two tabs: Upload Files and Connect Source. Accumulates sources into a basket.
 */
import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CloudUpload,
  Cable,
} from '@mui/icons-material';
import FileDropZone from './FileDropZone';
import ConnectorSourcePicker from './ConnectorSourcePicker';
import type { ConnectorSourceEntry } from './ConnectorSourcePicker';
import SourceBasket from './SourceBasket';
import { buildFileSignature } from './FileDropZone';

interface WizardStepSourcesProps {
  projectId: string | null;
  files: File[];
  connectorSources: ConnectorSourceEntry[];
  onFilesChange: (files: File[]) => void;
  onConnectorSourcesChange: (sources: ConnectorSourceEntry[]) => void;
  /** When true, hides the connector tab and shows files-only UI. */
  filesOnly?: boolean;
  /** Override accepted file types for the drop zone (default: all supported). */
  accept?: string;
  acceptLabel?: string;
}

const WizardStepSources: React.FC<WizardStepSourcesProps> = ({
  projectId,
  files,
  connectorSources,
  onFilesChange,
  onConnectorSourcesChange,
  filesOnly = false,
  accept,
  acceptLabel,
}) => {
  const [activeTab, setActiveTab] = useState<'files' | 'connector'>('files');

  const handleAddConnectorSources = useCallback(
    (entries: ConnectorSourceEntry[]) => {
      // Deduplicate by key
      const existing = new Set(connectorSources.map((e) => e.key));
      const newEntries = entries.filter((e) => !existing.has(e.key));
      onConnectorSourcesChange([...connectorSources, ...newEntries]);
    },
    [connectorSources, onConnectorSourcesChange],
  );

  const handleRemoveFile = useCallback(
    (signature: string) => {
      onFilesChange(files.filter((f) => buildFileSignature(f) !== signature));
    },
    [files, onFilesChange],
  );

  const handleRemoveConnectorSource = useCallback(
    (key: string) => {
      onConnectorSourcesChange(connectorSources.filter((e) => e.key !== key));
    },
    [connectorSources, onConnectorSourcesChange],
  );

  if (!filesOnly && !projectId) {
    return <Alert severity="warning">Select a project before adding sources.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Tab toggle — hidden in filesOnly mode */}
      {!filesOnly && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Data Source
          </Typography>
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={(_, mode) => { if (mode) setActiveTab(mode); }}
            fullWidth
            size="small"
          >
            <ToggleButton value="files">
              <CloudUpload sx={{ mr: 0.5 }} fontSize="small" />
              Upload Files
            </ToggleButton>
            <ToggleButton value="connector">
              <Cable sx={{ mr: 0.5 }} fontSize="small" />
              Connect Source
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Tab content */}
      {(filesOnly || activeTab === 'files') && (
        <FileDropZone files={files} onFilesChange={onFilesChange} accept={accept} acceptLabel={acceptLabel} />
      )}

      {!filesOnly && activeTab === 'connector' && projectId && (
        <ConnectorSourcePicker
          projectId={projectId}
          onAddSources={handleAddConnectorSources}
        />
      )}

      {/* Source basket */}
      <SourceBasket
        files={files}
        connectorSources={connectorSources}
        onRemoveFile={handleRemoveFile}
        onRemoveConnectorSource={handleRemoveConnectorSource}
      />
    </Box>
  );
};

export default WizardStepSources;
